import type { VoidFunctionComponent } from 'react';

import { FormLabelVariant, LabeledTextInput } from '../../../../../generic';
import SettingsPageHeader from '../../../../../shared/settings/SettingsPageHeader';
import usePoliciesSelection, { type PoliciesSelectionState } from '../../hooks/usePoliciesSelection';
import PoliciesSelection from '../../shared/ProjectsPage/ReleaseGroupsPolicies/PoliciesSelection/PoliciesSelection';
import { useReleaseGroupsSettings } from '../context';

import styles from './ReleaseGroupsSettingsGeneral.module.less';

/**
 * ReleaseGroupsSettings is the container for the general release groups settings pages.
 */
const ReleaseGroupsSettingsGeneral: VoidFunctionComponent = () => {
  const { data: settings, isDirty, onUpdate, onSave } = useReleaseGroupsSettings();
  const { title, policyId, securityPolicyId, qualityPolicyId } = settings;
  const {
    policies,
    isLoading: isLoadingPolicies,
    selectedPolicies,
    onUpdateSelectedPolicies,
    shouldDisplayLicensingPolicies,
    shouldDisplayQualityPolicies,
    shouldDisplaySecurityPolicies,
  } = usePoliciesSelection({
    licensingId: policyId,
    vulnerabilityId: securityPolicyId,
    qualityId: qualityPolicyId,
  });

  //  Update it in both the policies selection state and the settings state
  const handleUpdateSelectedPolicies = (nextSelectedPolicies: Partial<PoliciesSelectionState>) => {
    onUpdateSelectedPolicies(nextSelectedPolicies);
    onUpdate({
      policyId: nextSelectedPolicies.licensing?.value === undefined ? policyId : nextSelectedPolicies.licensing?.value,
      securityPolicyId:
        nextSelectedPolicies.vulnerability?.value === undefined
          ? securityPolicyId
          : nextSelectedPolicies.vulnerability?.value,
      qualityPolicyId:
        nextSelectedPolicies.quality?.value === undefined ? qualityPolicyId : nextSelectedPolicies.quality?.value,
    });
  };

  return (
    <>
      <SettingsPageHeader heading="General Settings">
        <SettingsPageHeader.SaveButton
          onClick={onSave}
          isDisabled={!isDirty}
        />
      </SettingsPageHeader>
      <div className={styles.title}>
        <LabeledTextInput
          label="Release Group Title"
          labelVariant={FormLabelVariant.Filter}
          size="small"
          placeholder="Release Group Title"
          value={title}
          onChange={(value) => onUpdate({ title: value })}
        />
      </div>
      <PoliciesSelection
        policies={policies}
        isLoading={isLoadingPolicies}
        selectedPolicies={selectedPolicies}
        onUpdateSelectedPolicies={handleUpdateSelectedPolicies}
        shouldDisplayLicensingPolicies={shouldDisplayLicensingPolicies}
        shouldDisplayQualityPolicies={shouldDisplayQualityPolicies}
        shouldDisplaySecurityPolicies={shouldDisplaySecurityPolicies}
      />
    </>
  );
};

export default ReleaseGroupsSettingsGeneral;
